import React, { createContext, useEffect, useState } from "react";
import { Routes, Route, useLocation } from 'react-router-dom';
import { getRoomMembers } from './api/rooms';

// pages
import Account from './pages/Account';
import Home from './pages/Home';
import Media from './pages/Media';
import Notifications from './pages/Notifications';
import Page404 from './pages/Page404';
import Room from './pages/Room';
import Topic from './pages/Topic';
import Topics from './pages/Topics';

// components
import Header from './components/Header';
import GlobalNavigarion from './components/GlobalNavigation';

// mui
import { useMediaQuery } from "@mui/material";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';

// css
import './styles/iconColor.css';

// types
type RoomMemberType = {
    userId: number,
    userName: string,
};

// theme
declare module '@mui/material/styles' {
    interface BreakpointOverrides {
        xs: true;
        sm: true;
        md: true;
        lg: true;
        xl: true;
        mb: true;
        tb: true;
        pc: true;
    }
}

const theme = createTheme({
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 900,
            lg: 1200,
            xl: 1536,
            mb: 0,
            tb: 768,
            pc: 1280,
        },
    },
});

// context
export const RoomMembersContext = createContext<RoomMemberType[] | undefined>(undefined);

export default function App() {
    const matches: boolean = useMediaQuery(theme.breakpoints.up("pc"));
    const location = useLocation();
    const roomId = location.pathname.split('/')[1];

    const [roomMembers, setRoomMembers] = useState<RoomMemberType[]>([]);
    useEffect(() => {
        (async () => {
            if (roomId && /^\d+$/.test(roomId)) {
                const response = await getRoomMembers(roomId);
                if (response) {
                    setRoomMembers(response);
                }
            }
        })()
    }, [roomId]);

    return (
        <RoomMembersContext.Provider value={roomMembers}>
            <ThemeProvider theme={theme}>
                <Header />
                <Toolbar />
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: {
                            mb: "column",
                            tb: "row",
                        },
                        flexWrap: "nowrap",
                        minHeight: "100vh",
                        backgroundColor: theme.palette.grey[100],
                    }}
                >
                    {matches &&
                        <Box
                            sx={{
                                flexBasis: 280,
                                flexGrow: 0,
                                flexShrink: 0,
                                backgroundColor: theme.palette.common.white,
                            }}
                        >
                            <GlobalNavigarion />
                        </Box>
                    }
                    <Box
                        sx={{
                            flexGrow: 1,
                        }}
                    >
                        <Routes>
                            <Route path="/" element={<Home />} />
                            <Route path="/account" element={<Account />} />
                            <Route path="/media" element={<Media />} />
                            <Route path="/notifications" element={<Notifications />} />
                            <Route path="/:roomId" element={<Room />} />
                            <Route path="/:roomId/topics" element={<Topics />} />
                            <Route path="/:roomId/topic/:topicId" element={<Topic />} />
                            <Route path={`/*`} element={<Page404 />} />
                        </Routes>
                    </Box>
                </Box>
            </ ThemeProvider>
        </RoomMembersContext.Provider>
    );
}